/* The console's clock formats instants in the viewer's chosen zone. Each server document
 * carries an immutable UTC/now snapshot through first hydration; browser subscriptions
 * then restore the device preference and advance relative time. No request mutates the
 * browser preference singleton. */

import { createContext, createElement, useContext, useSyncExternalStore, type ReactNode } from "react";

export type Clock = "local" | "utc";

/** One document's clock, carried with its router state rather than shared across requests. */
export interface ClockSnapshot {
  readonly clock: Clock;
  readonly timeZone: string;
  readonly now: number;
}
const RenderClock = createContext<ClockSnapshot | undefined>(undefined);
export function ClockProvider({ snapshot, children }: { readonly snapshot: ClockSnapshot; readonly children: ReactNode }) {
  return createElement(RenderClock.Provider, { value: snapshot }, children);
}

let minute = Date.now();
const minuteListeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | undefined;
function subscribeMinute(listener: () => void): () => void {
  minuteListeners.add(listener);
  minute = Date.now();
  ticker ??= setInterval(() => {
    minute = Date.now();
    minuteListeners.forEach(each => each());
  }, 60_000);
  return () => {
    minuteListeners.delete(listener);
    if (minuteListeners.size === 0 && ticker !== undefined) { clearInterval(ticker); ticker = undefined; }
  };
}
const currentMinute = () => minute;
const subscribeHydration = () => () => undefined;
const browserLive = () => true;
const serverSnapshot = () => false;

/** Snapshot while hydrating; the browser clock and minute ticker after commit. */
export function useClockSnapshot(): ClockSnapshot {
  const snapshot = useContext(RenderClock);
  const chosen = useClock();
  const zone = useSyncExternalStore(subscribeClock, timeZone, () => snapshot?.timeZone ?? timeZone());
  const now = useSyncExternalStore(subscribeMinute, currentMinute, () => snapshot?.now ?? minute);
  const live = useSyncExternalStore(subscribeHydration, browserLive, serverSnapshot);
  return { clock: chosen, timeZone: zone, now: live ? Math.max(now, Date.now()) : now };
}

let clock: Clock = "local";
const listeners = new Set<() => void>();

export function setClock(next: Clock): void {
  if (next === clock) return;
  clock = next;
  listeners.forEach((listener) => listener());
}

export function subscribeClock(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentClock(): Clock {
  return clock;
}

/** The clock, for the few components that must re-render when it changes. */
export function useClock(): Clock {
  const snapshot = useContext(RenderClock);
  return useSyncExternalStore(subscribeClock, currentClock, () => snapshot?.clock ?? currentClock());
}

/** The IANA zone the console reads time in. */
export function timeZone(): string {
  return clock === "utc" ? "UTC" : Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// ── Instants as text ─────────────────────────────────────────────────────────────

/** A protobuf `google.protobuf.Timestamp`, by shape. */
interface WireTimestamp {
  readonly seconds: bigint;
  readonly nanos: number;
}

/** An instant in any shape the console holds one: a Date, an ISO string, epoch
 *  milliseconds, or a wire timestamp. */
export type Instant = Date | string | number | WireTimestamp;

function dateOf(value: Instant): Date {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  return new Date(Number(value.seconds) * 1000 + Math.floor(value.nanos / 1_000_000));
}

const stampFormats = new Map<string, Intl.DateTimeFormat>();

function stampParts(at: Date, zone = timeZone()): Record<string, string> {
  let format = stampFormats.get(zone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
    stampFormats.set(zone, format);
  }
  return Object.fromEntries(format.formatToParts(at).map((part) => [part.type, part.value]));
}

function absent(value: Instant | null | undefined): value is null | undefined {
  return value === null || value === undefined || value === "";
}

/** An instant as the console shows it, 'YYYY-MM-DD HH:MM:SS' and the zone; "—" when absent. */
export function stamp(value: Instant | null | undefined, zone?: string): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "—";
  const p = stampParts(at, zone);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} ${p.timeZoneName}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** An instant as a row shows it, on the clock the header already names: '21:46' today,
 *  '28 Sep 21:46' this year, '28 Sep 2025' before; "—" when absent. `stamp` stays the
 *  exact form for a record's detail and an export. */
export function stampShort(value: Instant | null | undefined, snapshot?: Pick<ClockSnapshot, "now" | "timeZone">): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "—";
  const p = stampParts(at, snapshot?.timeZone);
  const now = stampParts(new Date(snapshot?.now ?? Date.now()), snapshot?.timeZone);
  const day = `${Number(p.day)} ${MONTHS[Number(p.month) - 1]}`;
  if (p.year !== now.year) return `${day} ${p.year}`;
  if (p.month === now.month && p.day === now.day) return `${p.hour}:${p.minute}`;
  return `${day} ${p.hour}:${p.minute}`;
}

/** An instant as a record's detail reads it, '28 Sep 2026, 21:46' on the clock; "—" when absent. */
export function stampLong(value: Instant | null | undefined, zone?: string): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "—";
  const p = stampParts(at, zone);
  return `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}, ${p.hour}:${p.minute}`;
}

/** How long ago (or how soon) an instant is, in words: 'just now', '4 min ago', '3 h ago',
 *  'yesterday', '5 days ago', 'in 2 h'. Past a week it is the day itself, as `stampShort`
 *  writes it without the hour. Empty when absent. */
export function ago(value: Instant | null | undefined, now: number = Date.now(), zone?: string): string {
  if (absent(value)) return "";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "";
  const seconds = Math.round((now - at.getTime()) / 1000);
  const future = seconds < 0;
  const span = Math.abs(seconds);
  const say = (amount: number, unit: string) =>
    future ? `in ${amount} ${unit}` : `${amount} ${unit} ago`;
  // Under 45 s either way is now: a server's clock a little ahead of the browser's, or a
  // reading that lands between the page's minute ticks, never reads as the future.
  if (span < 45) return "just now";
  if (span < 3600) return say(Math.max(1, Math.round(span / 60)), "min");
  if (span < 86_400) return say(Math.round(span / 3600), "h");
  const days = Math.round(span / 86_400);
  if (days === 1) return future ? "tomorrow" : "yesterday";
  if (days < 7) return say(days, "days");
  const p = stampParts(at, zone);
  const day = `${Number(p.day)} ${MONTHS[Number(p.month) - 1]}`;
  return p.year === stampParts(new Date(now), zone).year ? day : `${day} ${p.year}`;
}

/** An instant as a break in a conversation reads it, on the clock: 'Today 14:05',
 *  'Yesterday 09:12', 'Mon 12 Oct, 09:12' this year, 'Mon 12 Oct 2025, 09:12' before;
 *  "—" when absent. */
export function stampBreak(value: Instant | null | undefined, now: number = Date.now(), zone = timeZone()): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "—";
  const p = stampParts(at, zone);
  const clockTime = `${p.hour}:${p.minute}`;
  const day = calendarDay(at, zone);
  const todayOnClock = calendarDay(new Date(now), zone);
  if (day === todayOnClock) return `Today ${clockTime}`;
  if (day === addDays(todayOnClock, -1)) return `Yesterday ${clockTime}`;
  const weekday = WEEKDAYS[new Date(`${day}T00:00:00Z`).getUTCDay()];
  const label = `${weekday} ${Number(p.day)} ${MONTHS[Number(p.month) - 1]}`;
  return `${p.year === todayOnClock.slice(0, 4) ? label : `${label} ${p.year}`}, ${clockTime}`;
}

/** Whether a conversation shows a time break between two messages: at a day change on
 *  the clock, or after `gapMs` of quiet. */
export function isTimeBreak(
  previous: Instant | null | undefined,
  next: Instant | null | undefined,
  gapMs: number = 3_600_000,
  zone: string = timeZone(),
): boolean {
  if (absent(previous) || absent(next)) return false;
  const before = dateOf(previous);
  const after = dateOf(next);
  if (!Number.isFinite(before.getTime()) || !Number.isFinite(after.getTime())) return false;
  if (after.getTime() - before.getTime() >= gapMs) return true;
  return calendarDay(before, zone) !== calendarDay(after, zone);
}

/** The time of day alone, 'HH:MM:SS', for a live same-day feed; "—" when absent. */
export function stampTime(value: Instant | null | undefined, zone?: string): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  if (!Number.isFinite(at.getTime())) return "—";
  const p = stampParts(at, zone);
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** The calendar day alone, 'YYYY-MM-DD'; "—" when absent. */
export function stampDay(value: Instant | null | undefined, zone = timeZone()): string {
  if (absent(value)) return "—";
  const at = dateOf(value);
  return Number.isFinite(at.getTime()) ? calendarDay(at, zone) : "—";
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** A calendar day ('YYYY-MM-DD', no instant) as a person reads it: 'Thu 2 Oct' this year,
 *  'Thu 2 Oct 2025' before; "—" when absent. For days that are not moments — a payroll day,
 *  an earnings day — where `Time` would invent a midnight. */
export function dayLabel(day: string | null | undefined, snapshot?: Pick<ClockSnapshot, "now" | "timeZone">): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? "");
  if (match === null) return "—";
  const [, year, month, date] = match;
  const weekday = WEEKDAYS[new Date(Date.UTC(Number(year), Number(month) - 1, Number(date))).getUTCDay()];
  const label = `${weekday} ${Number(date)} ${MONTHS[Number(month) - 1]}`;
  return year === (snapshot ? calendarDay(new Date(snapshot.now), snapshot.timeZone) : today()).slice(0, 4) ? label : `${label} ${year}`;
}

/** Today's calendar day, 'YYYY-MM-DD'. */
export function today(zone = timeZone(), now = Date.now()): string {
  return calendarDay(new Date(now), zone);
}

/** A typed `datetime-local` value ('YYYY-MM-DDTHH:MM[:SS]') as the instant it names on
 *  the clock; undefined when it names none. */
export function wallClock(value: string | undefined): Date | undefined {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value ?? "");
  if (match === null) return undefined;
  const [, day, hour, minute, second] = match;
  const at = dayStart(day!, timeZone(), Number(hour) * 60 + Number(minute));
  return new Date(at.getTime() + Number(second ?? 0) * 1000);
}

/** An instant as a `datetime-local` input's value on the clock, 'YYYY-MM-DDTHH:MM'. */
export function wallClockValue(value: Instant): string {
  const p = stampParts(dateOf(value));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// ── Calendar arithmetic in one time zone ─────────────────────────────────────────

export interface LocalParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();

export function localParts(at: Date, timeZone: string): LocalParts {
  let format = partFormatters.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    partFormatters.set(timeZone, format);
  }
  const values: Record<string, number> = {};
  for (const part of format.formatToParts(at)) {
    if (part.type !== "literal") values[part.type] = Number(part.value);
  }
  return {
    year: values.year ?? 1970,
    month: values.month ?? 1,
    day: values.day ?? 1,
    hour: (values.hour ?? 0) % 24,
    minute: values.minute ?? 0,
  };
}

const pad = (value: number) => String(value).padStart(2, "0");

export function isoDay(year: number, month: number, day: number): string {
  // Normalise through UTC so day arithmetic (day - 6, day + 1) rolls months and years.
  const date = new Date(Date.UTC(year, month - 1, day));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function parseDay(day: string): { year: number; month: number; day: number } {
  const [year, month, date] = day.split("-").map(Number);
  return { year: year ?? 1970, month: month ?? 1, day: date ?? 1 };
}

/** The calendar day `at` falls on in `timeZone`, 'YYYY-MM-DD'. */
export function calendarDay(at: Date, timeZone: string): string {
  const parts = localParts(at, timeZone);
  return isoDay(parts.year, parts.month, parts.day);
}

export function addDays(day: string, days: number): string {
  const parts = parseDay(day);
  return isoDay(parts.year, parts.month, parts.day + days);
}

export function daysBetween(first: string, last: string): number {
  const a = parseDay(first);
  const b = parseDay(last);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) /
      86_400_000,
  );
}

/** The instant a calendar day begins in `timeZone`, or `minutes` into it on the wall clock. */
export function dayStart(day: string, timeZone: string, minutes = 0): Date {
  const { year, month, day: date } = parseDay(day);
  const guess = Date.UTC(year, month - 1, date, 0, minutes);
  const offset = (at: number) => {
    const parts = localParts(new Date(at), timeZone);
    return (
      Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute) -
      Math.floor(at / 60_000) * 60_000
    );
  };
  let instant = guess - offset(guess);
  // Across a daylight-saving change the offset at the answer differs from the guess's.
  const settled = offset(instant);
  if (guess - settled !== instant) instant = guess - settled;
  return new Date(instant);
}

export interface DayRange {
  readonly first: string;
  readonly last: string;
}

export interface InstantWindow {
  readonly start: Date;
  readonly end: Date;
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The instants a picked range of whole calendar days covers in `timeZone`, from the first
 *  day's midnight to the midnight after the last; undefined when a day is malformed, the
 *  range is reversed, or it spans more than `maxDays` days. */
export function pickedWindow(
  range: DayRange,
  timeZone: string,
  maxDays: number,
): InstantWindow | undefined {
  if (!DAY_PATTERN.test(range.first) || !DAY_PATTERN.test(range.last)) return undefined;
  const span = daysBetween(range.first, range.last);
  if (!(span >= 0 && span < maxDays)) return undefined;
  return {
    start: dayStart(range.first, timeZone),
    end: dayStart(addDays(range.last, 1), timeZone),
  };
}
